import { ChevronDown, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Divider } from "@/shared/ui/core";
import { Avatar } from "@/shared/ui/primitives";
import { cn } from "@/shared/lib/cn";
import { GlassPanel } from "../GlassPanel/GlassPanel";
import type { ShellUser } from "../AppShell/useShellUser";
import { useMenuPopover } from "./useMenuPopover";

export interface UserMenuProps {
  user: ShellUser;
  onLogout: () => void;
  className?: string;
}

/**
 * El menú de la persona conectada.
 *
 * ── Los roles ────────────────────────────────────────────────────────
 * El disparador declara `aria-haspopup="menu"`, `aria-expanded` y
 * `aria-controls`; sin ellos un lector de pantalla lo anuncia como un
 * botón cualquiera y nadie sabe que algo se abrió.
 *
 * La identidad (avatar, nombre, correo) queda FUERA del `role="menu"`, y
 * dentro del menú solo hay `role="menuitem"`. Es a propósito: un menú con
 * texto suelto entre los ítems es contenido que muchos lectores se saltan
 * —solo anuncian los menuitem— y el correo se perdería. Así el correo se
 * lee como lo que es, contenido del panel, y el menú tiene exactamente
 * las acciones que dice tener.
 *
 * ── El foco ──────────────────────────────────────────────────────────
 * Abrir, Escape, clic fuera y la devolución del foco los resuelve
 * `useMenuPopover`, que se extrajo de AQUÍ cuando el panel de
 * notificaciones necesitó las mismas cuatro reglas. Este menú le pasa
 * `'[role="menuitem"]'` para que al abrir el foco caiga en la primera
 * acción y no en el contenedor.
 */
export function UserMenu({ user, onLogout, className }: UserMenuProps) {
  const { t } = useTranslation();
  const { open, toggle, close, triggerRef, panelRef, panelId } = useMenuPopover({
    focusSelector: '[role="menuitem"]',
  });

  return (
    <div className={cn("relative shrink-0", className)}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        className="h-9 gap-2 border-ds-border bg-white/70 pl-1 pr-2.5"
      >
        <Avatar initials={user.initials} size="sm" aria-hidden="true" />
        {/*
          `sr-only` y no `hidden`: en pantallas estrechas el nombre no se
          ve, pero SIGUE siendo el nombre accesible del botón. Con
          `hidden` el botón se quedaba sin nombre en móvil —solo un avatar
          decorativo— y el lector lo anunciaba como "botón" a secas.
          Tampoco lleva `aria-label`: el nombre accesible es el texto que
          se ve, que es lo que espera quien maneja la interfaz por voz.
        */}
        <span className="sr-only max-w-[9rem] truncate text-[12.5px] text-ds-ink lg:not-sr-only lg:block">
          {user.name}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </Button>

      {open && (
        <GlassPanel
          ref={panelRef}
          id={panelId}
          className="absolute right-0 top-full z-50 mt-2 w-[16.5rem] bg-white/85 p-3 shadow-ds-float"
        >
          <div className="relative flex items-center gap-2.5">
            <Avatar initials={user.initials} size="md" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold leading-tight text-ds-ink">
                {user.name}
              </span>
              <span className="mt-0.5 block truncate text-[11.5px] leading-tight text-ds-ink-3">
                {user.email}
              </span>
            </span>
          </div>

          <Divider className="relative my-3" />

          <div role="menu" aria-label={t("Cuenta")} className="relative">
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                close();
                onLogout();
              }}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-2xl px-2.5 py-2 text-left text-[13px] font-semibold",
                "text-ds-ink-3 outline-none transition-colors duration-200 motion-reduce:transition-none",
                "hover:bg-ds-mist/70 hover:text-ds-violet-pop focus-visible:bg-ds-mist/70 focus-visible:text-ds-violet-pop",
              )}
            >
              <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
              {t("Cerrar sesión")}
            </button>
          </div>
        </GlassPanel>
      )}
    </div>
  );
}
